import React from 'react';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { Section, Panel, DataTable, UtilisationValue, InfoTip, StatRow, EmptyState } from './shared';
import { formatCount, formatPercent } from '../utils';
import { useInsightsStyles } from '../styles';

const TOTAL_LABELS = {
  granted: 'granted',
  allocated: 'allocated',
  assigned: 'assigned',
  unallocated_in_customer_pools: 'unallocated in customer pools',
  unassigned_in_projects: 'unassigned in projects',
};

/** `licences` section. */
const LicenceJourney = ({ data = {} }) => {
  const classes = useInsightsStyles();
  const funnel = data.course_funnel || [];
  const maxLog = Math.max(1, ...funnel.map(item => Math.log10(Math.max(1, Number(item.value) || 0))));
  const columns = [
    { key: 'label', label: 'Type', render: row => <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>{row.label}{row.note ? <InfoTip title={row.note} /> : null}</span> },
    { key: 'granted', label: 'Granted', align: 'right', render: row => formatCount(row.granted) },
    { key: 'allocated', label: 'Allocated', align: 'right', render: row => formatCount(row.allocated) },
    { key: 'assigned', label: 'Assigned', align: 'right', render: row => formatCount(row.assigned) },
    { key: 'unassigned_in_projects', label: 'Unassigned in projects', align: 'right', render: row => formatCount(row.unassigned_in_projects) },
    { key: 'utilisation_pct', label: 'Utilisation', render: row => <UtilisationValue value={row.utilisation_pct} overAssigned={row.over_assigned} /> },
  ];
  const totals = Object.entries(data.totals || {}).map(([key, value]) => ({ key, label: TOTAL_LABELS[key] || key.replace(/_/g, ' '), value }));
  return (
    <Section id="licences" title="Licence journey" exportKey="licences">
      <Panel>
        {totals.length ? <div style={{ marginBottom: 20 }}><StatRow items={totals} /></div> : null}
        <Grid container spacing={3}>
          <Grid item xs={12} lg={6}>
            <Typography component="h3" className={classes.sectionTitle}>Course seats, from granted to completed</Typography>
            {funnel.length ? funnel.map(item => {
              const width = Number(item.value) <= 0 ? 3 : Math.max(8, (Math.log10(Math.max(1, Number(item.value))) / maxLog) * 100);
              return (
                <div key={item.key} style={{ marginTop: 14 }} data-testid="licence-funnel-step">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 12, rowGap: 2 }}>
                    <Typography style={{ fontSize: '.8rem', fontWeight: 650, minWidth: 0 }}>{item.label}</Typography>
                    <Typography style={{ fontSize: '.8rem', whiteSpace: 'nowrap' }}>
                      <strong>{formatCount(item.value)}</strong>
                      {item.pct_of_previous !== null && item.pct_of_previous !== undefined ? <span className={classes.muted}> · {formatPercent(item.pct_of_previous)} of previous</span> : null}
                    </Typography>
                  </div>
                  <div style={{ height: 22, borderRadius: 7, background: 'var(--dm-row-alt, #f8fafc)', overflow: 'hidden', marginTop: 5 }}><div style={{ height: '100%', width: `${width}%`, background: 'linear-gradient(90deg,#3D7DDD,#60a5fa)', borderRadius: 7 }} /></div>
                  {item.description ? <Typography className={classes.description} style={{ marginTop: 4 }}>{item.description}</Typography> : null}
                </div>
              );
            }) : <div style={{ marginTop: 12 }}><EmptyState /></div>}
            {funnel.length ? <Typography className={classes.footnote}>Bar widths use a log scale so every stage stays visible.</Typography> : null}
          </Grid>
          <Grid item xs={12} lg={6}>
            <Typography component="h3" className={classes.sectionTitle} style={{ marginBottom: 10 }}>Seats by type</Typography>
            <DataTable compact ariaLabel="Seats by licence type" columns={columns} rows={data.by_type || []} />
          </Grid>
        </Grid>
      </Panel>
    </Section>
  );
};

export default LicenceJourney;
